import { Checkbox, CircularProgress, MenuItem, Switch, TableCell, TablePagination, TableSortLabel, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box, SystemStyleObject } from "@mui/system";
import _ from "lodash";
import React, { ChangeEvent } from "react";
import { ScrollingContent, StyledTable, StyledTableContainer, StyledTableLoader } from "./styles";
import AmountCard from "pages/billing/Dashboard/Amount.card";
import AmountFormat from "components/AmountFormat";
import { formattedAmount } from "utils/formattedAmount";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 75, 100];

export type ColumnType = {
  key: string;
  title: string | React.ReactElement;
  render?: (item: any) => React.ReactElement | string | null | number | boolean;
  hide?: boolean;
  default?: boolean;
  width?: string;
  sort?: boolean;
  active?: boolean;
  direction?: 'asc' | 'desc';
  align?: 'left' | 'center' | 'right';
  justifyContent?: '' | 'right' | 'flex-end' | 'center';
};

export type ExtendedColumnType = ColumnType & {
  render?: (
    item: any,
    getValues?: (row: any, event: ChangeEvent<HTMLInputElement>) => void
  ) => React.ReactElement | string | null | number;
};

type PaginationType = {
  totalCount: number;
  page: number;
  setPage: (page: number) => void;
  pageCount?: number;
  setPageCount?: (pageCount: number) => void;

};

type SelectionType = {
  selected: any[];
  setSelected: (selected: any[]) => void;
};

type FooterConfig = {
  columns: string[],
  totals: Record<string, number>
}

// sx?: any; //SystemStyleObject
interface TableProps {
  columns: Array<ColumnType>;
  sx?: any;
  data: any[];
  loading?: boolean;
  onRowClick?: (v: any) => void;
  sortHandler?: (v: any) => void;
  pagination?: PaginationType | null;
  selection?: SelectionType;
  heading?: string;
  defaultPageLimit?: null | number
  setDefaultLimit?: (v: any) => void;
  height?: string;
  columnTotals?: FooterConfig

}

function Table(props: TableProps) {
  const {
    columns,
    data = [],
    sx,
    pagination,
    loading = false,
    onRowClick,
    sortHandler,
    selection,
    heading,
    defaultPageLimit = null,
    setDefaultLimit,
    height = "70vh",
    columnTotals


  } = props;
  const { selected, setSelected } = selection || {
    selected: [],
    setSelected: () => { },
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(data);
    } else {
      setSelected([]);
    }
  };


  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, item: any) => {
    e.stopPropagation();
    if (e.target.checked) {
      setSelected([...selected, item]);
    } else {
      const filtered = selected?.filter((v: any) => v?.id !== item?.id);
      setSelected(filtered);
    }
  };

  const handleRowClick = (item: any) => {
    if (!onRowClick) return;
    onRowClick(item);
  };

  const handleCreateSortHandler = (v: any) => {
    if (!sortHandler) return;
    sortHandler(v);
  }

  const handlePageChange = (v: any, page: number) => {
    pagination?.setPage(page);
    setSelected([]);
  };

  const handleRowsPerPageChange = (e: any) => {
    if (pagination?.setPageCount) {
      pagination?.setPage(0);
      pagination?.setPageCount(+e.target.value);
      setSelected([]);
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = {
    base: { height: isMobile ? "auto" : height, overflow: "scroll" },
    enhance: sx,
  };

  // const styles = {
  //   height: '400px',
  //   overflow: 'scroll',
  //   ...sx
  // };

  return (
    <>
      <StyledTableContainer
        sx={{
          display: "flex",
          flexDirection: "column",
          height: height,
          position: "relative",
          ...sx,
        }}
      >

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "auto",
            position: "relative",
          }}
        >

          {heading && <Typography variant="h5" textAlign='center'>
            {heading}
          </Typography>}

          <StyledTable>
            <thead>
              <tr>
                {selection && data?.length > 0 && (
                  <th style={{ width: 50 }}>
                    <Checkbox
                      color="secondary"
                      onChange={handleSelectAll}
                      checked={selected?.length === data?.length}
                    />
                  </th>
                )}
                {columns.map((item, index) => {
                  if (item.hide) return null;
                  return (

                    <TableCell

                      component='th'
                      key={item.key}
                      sortDirection={false}
                      sx={{
                        width: item.width || 'auto',
                        fontWeight: 600,
                      }}
                    >
                      <Box display='inline-flex' justifyContent={item.justifyContent || ''}>
                        {item.sort ? (
                          <TableSortLabel
                            active={item?.active}
                            direction={item.direction}
                            onClick={() => handleCreateSortHandler(item)}
                            sx={{
                              m: 0,
                              p: 0,
                              gap: 0, // remove internal spacing
                              '& .MuiTableSortLabel-icon': {
                                opacity: item?.active ? 1 : 0.1,
                                color: 'red',
                              },
                              '&:hover .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: 'inherit',
                              },
                            }}
                          >
                            {item.title}
                          </TableSortLabel>
                        ) : (
                          item.title
                        )}
                      </Box>

                    </TableCell>
                  )

                }

                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "100%",
                    width: "100%",
                    background: "rgba(255,255,255,0.7)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10,
                  }}
                >
                  <CircularProgress color="primary" />
                </div>
              )}
              {(!loading && !data.length) ? (
                <tr>
                  <td
                    colSpan={100}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      textAlign: "center",
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "transparent"
                    }}
                  >
                    <Typography variant="subtitle2" color="rgba(0,0,0,0.5)">
                      No data
                    </Typography>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr key={index}>
                    {selection && (
                      <td>
                        <Checkbox
                          onChange={(e) => handleSelect(e, item)}
                          color="secondary"
                          checked={Boolean(_.find(selected, { id: item?.id }))}
                        />
                      </td>
                    )}

                    {columns.map((col, colIndex) => {
                      if (col.hide) return null;

                      return (
                        <td key={colIndex} onClick={() => handleRowClick(item)}>
                          {col?.render ? (
                            col.render(item)
                          ) : (
                            <Typography variant="body2">
                              {_.get(item, col.key)}
                            </Typography>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot >
              {columnTotals?.columns?.length &&

                <tr >
                  {columns.map((col, index) => {
                    const showTotal = columnTotals?.columns.includes(col.key);
                    const value = columnTotals?.totals?.[col.key]
                    return (
                      <td key={col.key} style={{ fontWeight: 'bold', backgroundColor: '#b1f2f1' }}>
                        {
                          showTotal && value !== undefined ? (
                            <AmountFormat
                              amount={formattedAmount(value)}
                              variant='h6'
                            />
                          ) : <Typography variant="h6"></Typography>
                        }

                      </td>
                    )
                  }


                  )}

                </tr>
              }
            </tfoot>
          </StyledTable>
        </div>

        {pagination && (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              borderTop: "1px solid rgba(0,0,0,0.1)",
              padding: "8px 16px",
              background: "#fff",
            }}
          >
            {setDefaultLimit && (
              <TextField
                select
                onChange={(e) => setDefaultLimit(e.target.value)}
                value={defaultPageLimit || ""}
                label="default"
                size="small"
                sx={{ width: 100, marginRight: 2 }}
              >
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="75">75</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </TextField>
            )}

            <TablePagination
              component="div"
              count={pagination.totalCount || 10}
              page={pagination.page || 0}
              onPageChange={handlePageChange}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              rowsPerPage={pagination.pageCount || 10}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        )}

        {/* {loading && (
          <StyledTableLoader>
            <CircularProgress color="primary" />
          </StyledTableLoader>
        )}
        {!loading && !data.length ? (
          <StyledTableLoader>
            <Typography variant="subtitle2" color="rgba(0,0,0,0.5)">
              No data
            </Typography>
          </StyledTableLoader>
        ) : null} */}
      </StyledTableContainer >
    </>

  );
}


export default Table;
